/** @jsxImportSource @emotion/react */
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../reducers";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { resolveTenantPath } from "../../../../utils/tenantUrl";
import { Breadcrumbs } from "../../../Helper/Breadcrumbs";
import { DATE_URL_FORMAT } from "../../Ripening/hooks/useRipeningRoom";

import { useRouteLayer } from "../../../../hooks/useCurrentEntities";

interface LayerBreadcrumbsProps {
    check?: {
        group_label: string;
        location_id: string;
        created: string;
    };
    reportTitle?: string;
}

const LayerBreadcrumbs: React.FC<LayerBreadcrumbsProps> = ({ check = undefined, reportTitle }) => {
    const layer = useRouteLayer();
    const tenantId = useSelector((state: RootState) => state.auth.user?.tenant?.tenant_id);
    const config = useConfig();
    const layer_config = config.get_layer_config(layer);
    const location_config = check ? config.get_check_type_config(layer, check as any) : undefined;

    const renderLayerLabel = (label: string) => {
        if (label?.length > 12) {
            return `${label.slice(0, 4)}...${label.slice(-4)}`;
        }

        return label;
    };

    const parent = layer.parents && layer.parents.length > 0 ? layer.parents.at(-1) : false;
    const parent_layer_type = parent ? config.get_raw_layer_config(parent.type) : false;
    const ripeningRoomUrl = check?.location_id ? `/ripening-room/${check.location_id}?fromDate=${
        dayjs(check.created)
            .subtract(config.root_config.ripening_room_days_before || 2, "day")
            .format(DATE_URL_FORMAT)
    }&toDate=${
        dayjs(check.created)
            .add(config.root_config.ripening_room_days_after || 2, "day")
            .format(DATE_URL_FORMAT)
    }` : `/layer/${layer.id}`;

    const breadcrumbItems = useMemo(() => {
        const items: any = [];

        if (parent && parent_layer_type) {
            items.push({ label: `${parent_layer_type?.text} ${renderLayerLabel(parent.label)}`, url: resolveTenantPath(`/layer/${parent.id}`, tenantId), isActive: false });
        }

        if (check?.location_id) {
            items.push({ label: config.get_form_option_label("location_id", check.location_id), url: ripeningRoomUrl, isActive: false });

            if (check?.group_label) {
                items.push({ label: check.group_label, url: location_config ? ripeningRoomUrl : undefined, isActive: !location_config });
            }
        } else {
            items.push({ label: `${layer_config?.text} ${renderLayerLabel(layer.label)}`, url: resolveTenantPath(`/layer/${layer.id}`, tenantId), isActive: !location_config && !reportTitle });
        }

        if (typeof location_config === "object") {
            items.push({ label: `${location_config?.text} Check`, url: undefined, isActive: !reportTitle });
        }

        if (reportTitle) {
            items.push({ label: reportTitle, url: undefined, isActive: true });
        }

        return items;
    }, [layer_config, layer, check, config, parent, parent_layer_type, ripeningRoomUrl, location_config]);

    if (!layer_config) {
        return null;
    }

    return (
        <Breadcrumbs
            homeUrl="/layer"
            items={breadcrumbItems}
            withBackButton
            withLocationTracker
        />
    );
};

export default LayerBreadcrumbs;
